import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { GanttChart, type GanttRow } from "./gantt-chart";
import { RichTextEditor } from "./rich-text-editor";
import type { TiptapDoc } from "./rich-text-doc";
import { FieldPicker } from "./task-workspace";
import { ErrorBanner, RequiredMark, Skeleton, useConfirm, useModalA11y, useToast } from "./ui-kit";

type GoalStatus = "planned" | "active" | "at_risk" | "done" | "cancelled";

type Goal = {
  createdAt: string;
  description: TiptapDoc | null;
  endsOn: string | null;
  id: string;
  name: string;
  ownerId: string | null;
  parentGoalId: string | null;
  startsOn: string | null;
  status: GoalStatus;
  updatedAt: string;
};

type TimelineRow = {
  contextOnly: boolean;
  depth: number;
  id: string;
  kind: "goal" | "work_item";
  label: string;
  progress?: { donePoints: number; percent: number; totalPoints: number; unestimatedCount: number };
  range: { end: string | null; source: "due_date" | "explicit" | "none" | "sprint"; start: string | null };
};

type TimelineResponse = {
  rows: TimelineRow[];
  unscheduled: Array<{ goalId: string; workItems: Array<{ id: string; taskKey: string | null; title: string }> }>;
};

type WorkItemSearchResult = { id: string; taskKey: string | null; title: string; type: string };

type Member = { displayName: string; id: string };

const STATUS_LABEL: Record<GoalStatus, string> = {
  active: "Active", at_risk: "At risk", cancelled: "Cancelled", done: "Done", planned: "Planned",
};

const api = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init.headers } : init?.headers,
  });
  const payload = (await response.json()) as T & { message?: string };
  if (!response.ok) throw new Error(payload.message || "Taskei could not complete that request.");
  return payload;
};

export function GoalsPage({ isAdmin, members, teamId }: { isAdmin: boolean; members: Member[]; teamId: string }) {
  const [goals, setGoals] = useState<Goal[] | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Goal | null>(null);
  const [linkingGoalId, setLinkingGoalId] = useState<string | null>(null);
  const showToast = useToast();
  const confirm = useConfirm();

  const load = useCallback(async () => {
    const [goalsResult, timelineResult] = await Promise.all([
      api<{ goals: Goal[]; permissions: string[] }>(`/api/goals?teamId=${encodeURIComponent(teamId)}`),
      api<TimelineResponse>(`/api/teams/${encodeURIComponent(teamId)}/timeline`),
    ]);
    setGoals(goalsResult.goals);
    setPermissions(goalsResult.permissions);
    setTimeline(timelineResult);
  }, [teamId]);

  useEffect(() => {
    void load().catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load goals."));
  }, [load]);

  const canManage = isAdmin || permissions.includes("manage_sprints");
  const goalsById = useMemo(() => new Map((goals ?? []).map((goal) => [goal.id, goal])), [goals]);
  const ganttRows = useMemo<GanttRow[]>(
    () => (timeline?.rows ?? []).map((row) => ({
      contextOnly: row.contextOnly, depth: row.depth, end: row.range.end, id: row.id, kind: row.kind,
      label: row.label, percent: row.progress?.percent, source: row.range.source, start: row.range.start,
    })),
    [timeline],
  );

  const deleteGoal = async (goal: Goal) => {
    const confirmed = await confirm({
      confirmLabel: "Delete goal",
      description: `"${goal.name}" will be deleted. Sub-goals will be kept, unparented. Linked work items are only unlinked, never deleted.`,
      title: `Delete ${goal.name}?`,
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await api(`/api/goals/${goal.id}`, { method: "DELETE" });
      showToast("Goal deleted.", "success");
      await load();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not delete goal.", "error");
    }
  };

  if (!goals || !timeline) {
    return error ? <ErrorBanner>{error}</ErrorBanner> : <Skeleton label="Loading goals…" variant="list" />;
  }

  return (
    <section className="goals-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Multi-month planning</p>
          <h2>All goals</h2>
          <p className="section-description">Track multi-month goals and how the linked epics and stories are tracking against them.</p>
        </div>
        {canManage && (
          <button className="button primary" onClick={() => setCreating(true)} type="button">+ New goal</button>
        )}
      </div>
      <ErrorBanner>{error}</ErrorBanner>
      {ganttRows.length > 0 && <GanttChart rows={ganttRows} />}
      {goals.length ? (
        <ul className="goal-list">
          {goals.map((goal) => (
            <GoalRow
              canManage={canManage}
              goal={goal}
              key={goal.id}
              linking={linkingGoalId === goal.id}
              onDelete={() => void deleteGoal(goal)}
              onEdit={() => setEditing(goal)}
              onLinkingChange={(open) => setLinkingGoalId(open ? goal.id : null)}
              onLinked={() => void load()}
              parentGoal={goal.parentGoalId ? goalsById.get(goal.parentGoalId) ?? null : null}
              teamId={teamId}
              unscheduled={timeline.unscheduled.find((entry) => entry.goalId === goal.id)?.workItems ?? []}
            />
          ))}
        </ul>
      ) : (
        <div className="empty-state compact">
          <h2>No goals yet</h2>
          <p>Create a goal and link epics or stories to it to see progress on the timeline above.</p>
        </div>
      )}
      {(creating || editing) && (
        <GoalFormDialog
          allGoals={goals}
          editing={editing}
          members={members}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={async () => { setCreating(false); setEditing(null); await load(); }}
          teamId={teamId}
        />
      )}
    </section>
  );
}

function GoalRow({
  canManage, goal, linking, onDelete, onEdit, onLinkingChange, onLinked, parentGoal, teamId, unscheduled,
}: {
  canManage: boolean;
  goal: Goal;
  linking: boolean;
  onDelete: () => void;
  onEdit: () => void;
  onLinkingChange: (open: boolean) => void;
  onLinked: () => void;
  parentGoal: Goal | null;
  teamId: string;
  unscheduled: Array<{ id: string; taskKey: string | null; title: string }>;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<WorkItemSearchResult[]>([]);
  const [busy, setBusy] = useState(false);
  const showToast = useToast();

  useEffect(() => {
    if (!linking || !query) { setResults([]); return; }
    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await api<{ tasks: WorkItemSearchResult[] }>(
          `/api/tasks?teamId=${encodeURIComponent(teamId)}&query=${encodeURIComponent(query)}`,
        );
        if (!cancelled) setResults(result.tasks.slice(0, 8));
      } catch {
        if (!cancelled) setResults([]);
      }
    }, 200);
    return () => { cancelled = true; window.clearTimeout(timeoutId); };
  }, [linking, query, teamId]);

  const link = async (workItemId: string) => {
    setBusy(true);
    try {
      await api(`/api/goals/${goal.id}/links`, { body: JSON.stringify({ workItemId }), method: "POST" });
      setQuery(""); setResults([]);
      onLinked();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not link that item.", "error");
    } finally { setBusy(false); }
  };

  const unlink = async (workItemId: string) => {
    setBusy(true);
    try {
      await api(`/api/goals/${goal.id}/links/${workItemId}`, { method: "DELETE" });
      onLinked();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not unlink that item.", "error");
    } finally { setBusy(false); }
  };

  return (
    <li className="goal-row">
      <article>
        <div className="goal-row-header">
          <div>
            <span className={`stage-pill goal-status-${goal.status}`}>{STATUS_LABEL[goal.status]}</span>
            <h3>{goal.name}</h3>
            {parentGoal && <p className="goal-parent-chip">Sub-goal of {parentGoal.name}</p>}
            {(goal.startsOn || goal.endsOn) && (
              <p className="goal-dates">{goal.startsOn ?? "…"} → {goal.endsOn ?? "…"}</p>
            )}
          </div>
          {canManage && (
            <div className="goal-row-actions">
              <button className="text-button" onClick={onEdit} type="button">Edit</button>
              <button className="text-button" onClick={() => onLinkingChange(!linking)} type="button">
                {linking ? "Close" : "+ Link work item"}
              </button>
              <button className="button danger" onClick={onDelete} type="button">Delete</button>
            </div>
          )}
        </div>
        {linking && (
          <div className="goal-link-picker">
            <label>
              Search epics and stories<RequiredMark />
              <input
                aria-label={`Search work items to link to ${goal.name}`}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Key or title"
                value={query}
              />
            </label>
            {results.length > 0 && (
              <ul className="goal-link-results">
                {results.map((result) => (
                  <li key={result.id}>
                    <button disabled={busy} onClick={() => void link(result.id)} type="button">
                      {result.taskKey ? `${result.taskKey} · ` : ""}{result.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {unscheduled.length > 0 && (
          <div className="goal-unscheduled">
            <p className="goal-unscheduled-heading">Unscheduled ({unscheduled.length})</p>
            <ul>
              {unscheduled.map((item) => (
                <li key={item.id}>
                  <span>{item.taskKey ? `${item.taskKey} · ` : ""}{item.title}</span>
                  {canManage && (
                    <button className="text-button" onClick={() => void unlink(item.id)} type="button">Unlink</button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </article>
    </li>
  );
}

function GoalFormDialog({ allGoals, editing, members, onClose, onSaved, teamId }: {
  allGoals: Goal[];
  editing: Goal | null;
  members: Member[];
  onClose: () => void;
  onSaved: () => Promise<void>;
  teamId: string;
}) {
  const dialogRef = useModalA11y<HTMLElement>(onClose);
  const [description, setDescription] = useState<TiptapDoc | null>(editing?.description ?? null);
  const [parentGoalId, setParentGoalId] = useState(editing?.parentGoalId ?? "");
  const [ownerId, setOwnerId] = useState(editing?.ownerId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const parentOptions = useMemo(
    () => [{ id: "", name: "No parent goal" }, ...allGoals.filter((goal) => goal.id !== editing?.id)],
    [allGoals, editing],
  );
  const ownerOptions = useMemo(() => [{ displayName: "Unassigned", id: "" }, ...members], [members]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setError("");
    const form = new FormData(event.currentTarget);
    const body = {
      description,
      endsOn: String(form.get("endsOn") ?? "") || null,
      name: String(form.get("name") ?? ""),
      ownerId: ownerId || null,
      parentGoalId: parentGoalId || null,
      startsOn: String(form.get("startsOn") ?? "") || null,
      ...(editing ? { status: String(form.get("status") ?? editing.status) } : {}),
      ...(editing ? {} : { teamId }),
    };
    try {
      if (editing) await api(`/api/goals/${editing.id}`, { body: JSON.stringify(body), method: "PATCH" });
      else await api("/api/goals", { body: JSON.stringify(body), method: "POST" });
      await onSaved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not save goal.");
    } finally { setBusy(false); }
  };

  return (
    <div className="dialog-backdrop" onMouseDown={onClose} role="presentation">
      <section aria-modal="true" className="dialog" onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog">
        <button className="dialog-close" onClick={onClose} type="button">×</button>
        <p className="eyebrow">Goals</p>
        <h2>{editing ? "Edit goal" : "New goal"}</h2>
        <ErrorBanner>{error}</ErrorBanner>
        <form onSubmit={submit}>
          <label><span>Name<RequiredMark /></span><input autoFocus defaultValue={editing?.name} name="name" required /></label>
          <label>Description<RichTextEditor ariaLabel="Goal description" characterLimit={10000} onChange={setDescription} placeholder="What does success look like?" value={description} /></label>
          <div className="goal-form-dates">
            <label><span>Starts</span><input defaultValue={editing?.startsOn ?? ""} name="startsOn" type="date" /></label>
            <label><span>Target</span><input defaultValue={editing?.endsOn ?? ""} name="endsOn" type="date" /></label>
          </div>
          {editing && (
            <label>
              Status
              <select defaultValue={editing.status} name="status">
                {(Object.keys(STATUS_LABEL) as GoalStatus[]).map((status) => (
                  <option key={status} value={status}>{STATUS_LABEL[status]}</option>
                ))}
              </select>
            </label>
          )}
          <label>
            <span>Parent goal</span>
            <FieldPicker
              ariaLabel="Parent goal"
              items={parentOptions}
              name="parentGoalId"
              onSelect={setParentGoalId}
              renderLabel={(item) => item.name}
              renderTrigger={parentOptions.find((item) => item.id === parentGoalId)?.name ?? "No parent goal"}
              selectedId={parentGoalId}
            />
          </label>
          <label>
            <span>Owner</span>
            <FieldPicker
              ariaLabel="Goal owner"
              items={ownerOptions}
              name="ownerId"
              onSelect={setOwnerId}
              renderLabel={(item) => item.displayName}
              renderTrigger={ownerOptions.find((item) => item.id === ownerId)?.displayName ?? "Unassigned"}
              selectedId={ownerId}
            />
          </label>
          <div className="dialog-actions">
            <button className="button secondary" onClick={onClose} type="button">Cancel</button>
            <button className="button primary" disabled={busy} type="submit">{editing ? "Save changes" : "Create goal"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}
